import { Lock, Unlock, RotateCcw, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { useState, useEffect } from 'react';

export function SimulatorUI({ lockState, setLockState }: any) {
  const [timer, setTimer] = useState(0);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      if (!lockState.isOpen) setTimer(t => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [lockState.isOpen]);

  useEffect(() => {
    if (lockState.feedback) {
      setFeedback(lockState.feedback);
      const timer = setTimeout(() => setFeedback(''), 2500);
      return () => clearTimeout(timer);
    }
  }, [lockState.feedback]);
  const handlePickPin = (index: number) => {
    const newPinsLifted = [...lockState.pinsLifted];
    newPinsLifted[index] = !newPinsLifted[index];
    setLockState((prev: any) => ({
      ...prev,
      pinsLifted: newPinsLifted,
      feedback: !newPinsLifted[index] ? 'Pin reset' : `Pin ${index + 1} set!`,
    }));

    if (newPinsLifted.every(p => p) && !lockState.isOpen) {
      setLockState((prev: any) => ({
        ...prev,
        isOpen: true,
        feedback: 'Lock opened!',
      }));
    }
  };

  const applyTension = () => {
    setLockState((prev: any) => ({
      ...prev,
      rotation: (prev.rotation + 8) % 360,
    }));
  };

  const handleReset = () => {
    setLockState({
      rotation: 0,
      pinsLifted: [false, false, false, false, false],
      selectedTool: 'pick',
      difficulty: 'medium',
      isOpen: false,
      feedback: '',
    });
    setTimer(0);
    setFeedback('');
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const pinsSet = lockState.pinsLifted.filter((p: boolean) => p).length;
  const progress = (pinsSet / 5) * 100;
  return (
    <div className="absolute inset-0 pointer-events-none">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 via-black/40 to-transparent p-6 pointer-events-auto">
        <div className="flex justify-between items-center max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Lock className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Lock Simulator</h1>
              <p className="text-xs text-slate-400">Practice lockpicking</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-mono font-bold text-amber-400">{formatTime(timer)}</div>
            <p className="text-xs text-slate-400 mt-1">Elapsed Time</p>
          </div>
        </div>
      </div>

      {/* Center Feedback */}
      {feedback && (
        <div
          className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto px-8 py-4 rounded-xl text-center font-semibold text-lg animate-in fade-in zoom-in ${
            feedback.includes('opened')
              ? 'bg-emerald-500/95 text-white shadow-lg shadow-emerald-500/50'
              : feedback.includes('set')
              ? 'bg-blue-500/95 text-white shadow-lg shadow-blue-500/50'
              : 'bg-slate-700/95 text-slate-100'
          }`}
        >
          {feedback}
        </div>
      )}

      {/* Left Panel - Tension Control */}
      <div className="absolute left-6 top-1/2 transform -translate-y-1/2 space-y-4 pointer-events-auto">
        <div className="bg-slate-900/80 backdrop-blur border border-slate-700 rounded-lg p-4 w-48">
          <p className="text-xs font-semibold text-slate-300 mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" />
            Tension
          </p>
          <Button
            onClick={applyTension}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold mb-3"
          >
            Apply Tension
          </Button>
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-slate-400">
              <span>Rotation</span>
              <span className="text-amber-400 font-mono">{lockState.rotation}°</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div
                className="bg-amber-400 h-full rounded-full transition-all"
                style={{ width: `${(lockState.rotation / 360) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Pin Controls */}
      <div className="absolute right-6 top-1/2 transform -translate-y-1/2 space-y-3 pointer-events-auto">
        <div className="bg-slate-900/80 backdrop-blur border border-slate-700 rounded-lg p-4 w-56">
          <p className="text-xs font-semibold text-slate-300 mb-4">Pin Stack</p>
          <div className="space-y-2 mb-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <Button
                key={i}
                variant={lockState.pinsLifted[i] ? 'default' : 'outline'}
                size="sm"
                onClick={() => handlePickPin(i)}
                className={`w-full justify-between ${
                  lockState.pinsLifted[i]
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border-slate-600'
                }`}
              >
                <span>Pin {i + 1}</span>
                {lockState.pinsLifted[i] && <Unlock className="w-3 h-3" />}
              </Button>
            ))}
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-slate-400">
              <span>Progress</span>
              <span className="text-emerald-400 font-mono">{pinsSet}/5</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-emerald-500 to-emerald-400 h-full rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-3 pointer-events-auto">
        <Button
          onClick={handleReset}
          variant="outline"
          className="bg-slate-900/80 hover:bg-slate-800 text-white border-slate-700"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset
        </Button>
        {lockState.isOpen && (
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-lg shadow-emerald-500/30">
            <Lock className="w-4 h-4 mr-2" />
            Successfully Opened!
          </Button>
        )}
      </div>

      {/* Info Panel - Bottom Right */}
      <div className="absolute bottom-6 right-6 bg-slate-900/80 backdrop-blur border border-slate-700 rounded-lg p-4 pointer-events-auto max-w-xs">
        <div className="text-xs text-slate-400 space-y-3">
          <div className="flex justify-between">
            <span>Difficulty</span>
            <span className="text-slate-300 font-semibold capitalize">{lockState.difficulty}</span>
          </div>
          <div className="h-px bg-slate-700" />
          <div className="text-slate-500 text-xs leading-relaxed">
            <p className="font-semibold text-slate-400 mb-1">How to Pick:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Apply tension with the wrench</li>
              <li>Use the pick to lift each pin</li>
              <li>Set all 5 pins to open the lock</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}